import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, History, Trash2, MessageSquareText, Languages, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Topbar } from "@/components/Topbar";
import { TranslationCard } from "@/components/TranslationCard";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { ConversationMode } from "@/components/ConversationMode";
import { useLearning } from "@/context/LearningContext";
import { toast } from "sonner";

export default function Translator() {
  const { t } = useI18n();
  const { learn } = useLearning();
  const [text, setText] = useState("");
  const [target, setTarget] = useState("auto");
  const [mode, setMode] = useState("translate");
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const endRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/translations");
      setHistory(data);
    } catch {}
  };

  useEffect(() => { loadHistory(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [results]);

  const doTranslate = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/translate", { text, target_lang: target });
      setResults((r) => [...r, data]);
      setText("");
      loadHistory();
      learn(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Translation failed");
    } finally {
      setLoading(false);
    }
  };

  const onVoiceResult = (data) => {
    setResults((r) => [...r, data]);
    loadHistory();
    learn(data);
  };

  const removeHistory = async (id) => {
    await api.delete(`/translations/${id}`);
    loadHistory();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Topbar />
      <div className="flex-1 max-w-3xl w-full mx-auto flex flex-col">
        {/* Mode switch */}
        <div className="flex items-center justify-center px-4 pt-3">
          <div className="inline-flex rounded-full bg-secondary p-1" data-testid="mode-toggle">
            <button
              onClick={() => setMode("translate")}
              data-testid="mode-translate"
              className={`flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-medium transition-colors ${mode === "translate" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
            >
              <Languages size={16} /> {t("translateMode")}
            </button>
            <button
              onClick={() => setMode("conversation")}
              data-testid="mode-conversation"
              className={`flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-medium transition-colors ${mode === "conversation" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
            >
              <Users size={16} /> {t("convMode")}
            </button>
          </div>
        </div>

        {mode === "conversation" ? (
          <div className="flex-1 min-h-[60vh] flex flex-col">
            <ConversationMode />
          </div>
        ) : (
          <>
        {/* Controls */}
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <div className="inline-flex rounded-full bg-secondary p-1" data-testid="target-lang-toggle">
            <button
              onClick={() => setTarget("auto")}
              data-testid="target-auto"
              className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${target === "auto" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
            >
              {t("autoDetect")}
            </button>
            <button
              onClick={() => setTarget("en")}
              data-testid="target-en"
              className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${target === "en" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
            >
              EN
            </button>
            <button
              onClick={() => setTarget("ar")}
              data-testid="target-ar"
              className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${target === "ar" ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
            >
              ع
            </button>
          </div>
          <button
            onClick={() => setShowHistory((s) => !s)}
            data-testid="toggle-history"
            className="flex items-center gap-1.5 h-9 px-3 rounded-full border border-border text-sm hover:bg-secondary"
          >
            <History size={16} /> {t("history")}
          </button>
        </div>

        {/* Chat area */}
        <div className="flex-1 chat-pattern overflow-y-auto scroll-thin px-4 py-6 space-y-6 min-h-[50vh]">
          {results.length === 0 && (
            <div className="h-full grid place-items-center text-center text-muted-foreground py-16">
              <div>
                <div className="h-16 w-16 rounded-2xl bg-white grid place-items-center mx-auto mb-4 shadow-sm">
                  <MessageSquareText className="text-accent" size={28} />
                </div>
                <p className="font-medium text-foreground">{t("typeMessage")}</p>
                <p className="text-sm mt-1">{t("recordOrUpload")}</p>
              </div>
            </div>
          )}
          {results.map((r) => (
            <TranslationCard key={r.id} record={r} />
          ))}
          <div ref={endRef} />
        </div>

        {/* Input bar */}
        <div className="border-t border-border bg-white p-3 space-y-2">
          <div className="flex items-end gap-2">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); doTranslate(); } }}
              placeholder={t("typeMessage")}
              data-testid="translate-input"
              rows={1}
              className="flex-1 resize-none rounded-2xl border border-border px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-accent/40 max-h-32"
            />
            <button
              onClick={doTranslate}
              disabled={loading || !text.trim()}
              data-testid="translate-send-btn"
              className="h-12 w-12 shrink-0 grid place-items-center rounded-full bg-accent text-white disabled:opacity-50 hover:bg-accent/90 transition-colors"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} className="rtl:rotate-180" />}
            </button>
          </div>
          <VoiceRecorder targetLang={target} onResult={onVoiceResult} busy={voiceBusy} setBusy={setVoiceBusy} />
        </div>
          </>
        )}
      </div>

      {/* History drawer */}
      {showHistory && (
        <div className="fixed inset-0 z-40 flex justify-end" onClick={() => setShowHistory(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative w-full max-w-md bg-background h-full overflow-y-auto scroll-thin p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-head font-bold text-xl mb-4 flex items-center gap-2"><History size={20} /> {t("history")}</h3>
            {history.length === 0 && <p className="text-muted-foreground text-sm">{t("noHistory")}</p>}
            <div className="space-y-3">
              {history.map((h) => (
                <div key={h.id} className="bg-white border border-border rounded-xl p-3" data-testid="history-item">
                  <div className="flex justify-between items-start gap-2">
                    <p className="text-sm text-muted-foreground line-clamp-2 flex-1" dir={h.detected_language === "ar" ? "rtl" : "ltr"}>
                      {h.kind === "voice" && "🎙 "}{h.source_text}
                    </p>
                    <button onClick={() => removeHistory(h.id)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid="delete-history">
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <p className="text-sm font-medium text-primary mt-1.5" dir={h.target_lang === "ar" ? "rtl" : "ltr"}>{h.translation}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
